/**
 * `useMessages(context)` — real-time thread hook for the messaging platform
 * (M-MSG-2). Subscribes to a single thread, exposes an ordered message list plus
 * `send`/`markRead` actions (architecture-messaging-platform.md §9.1).
 *
 * Role/scope derivation (critical, §9.1): `send()` reads `activeRole`,
 * `effectiveScopeUid` and `currentUser.uid` from `useAuth()` and passes them to
 * {@link sendMessage}. `authorRole = activeRole === 'advisor' ? 'advisor' : 'owner'`.
 * The caller supplies ONLY `text` — never `authorRole`/`userId`.
 *
 * Interface deviation from §9.1 (documented in changelog): `useMessages` accepts
 * an additional optional `refCollection` argument. `MessageContext` carries no
 * `refCollection`, but a `receipt` create requires it (§3.4 / Rules §4.2). The
 * argument is additive and unused for `general`/`invoice` threads.
 *
 * §3.3 own-message-unread compensation: `getThreadUnreadCount`/`getGlobalUnreadCount`
 * cannot exclude the viewer's own messages server-side (Firestore forbids a second
 * inequality alongside `createdAt >`). `send()` compensates at the hook level: AFTER
 * `sendMessage` resolves successfully, it advances both the thread and the global
 * read-cursor to now (`markThreadRead` + `markGlobalRead`), so the sender's own
 * just-sent message never shows as unread. A cursor-advance failure is logged but
 * never turns an already-successful send into a rejected promise.
 *
 * @module useMessages
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Message,
  MessageContext,
  MessageAuthorRole,
  ReceiptRefCollection,
} from '../types/messagingTypes';
import { subscribeThread, sendMessage, markThreadRead, markGlobalRead } from '../api/messagesApi';
import logger from '../utils/logger';

/** Return contract of {@link useMessages} (§9.1). */
export interface UseMessagesResult {
  messages: Message[];
  loading: boolean;
  error: string | null;
  /** Sends `text` in the hook's context; rejects (and sets `error`) on failure. */
  send: (text: string) => Promise<void>;
  /** Fire-and-forget: marks the thread read for the current viewer. */
  markRead: () => void;
}

/**
 * Subscribes to `context`'s thread and exposes send/markRead.
 *
 * @param context - The thread to load (`general`/`invoice`/`receipt`).
 * @param refCollection - Required for `receipt` contexts to disambiguate the
 *   receipt entity on send (§3.4); ignored otherwise.
 * @returns {@link UseMessagesResult}.
 */
export function useMessages(
  context: MessageContext,
  refCollection?: ReceiptRefCollection | null,
): UseMessagesResult {
  const { currentUser, activeRole, effectiveScopeUid } = useAuth();

  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const ctxType = context.type;
  const ctxRefId = context.refId ?? null;

  // Stabilize the context object so the subscription effect only re-runs when the
  // primitive identity of the thread changes (callers often pass a new literal).
  const stableContext = useMemo<MessageContext>(
    () => ({ type: ctxType, refId: ctxRefId }),
    [ctxType, ctxRefId],
  );

  useEffect(() => {
    if (!currentUser) {
      setMessages([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const unsubscribe = subscribeThread(
      stableContext,
      (msgs) => {
        setMessages(msgs);
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [currentUser, stableContext]);

  const send = useCallback(
    async (text: string): Promise<void> => {
      if (!currentUser) {
        const msg = 'useMessages.send: no authenticated user.';
        setError(msg);
        throw new Error(msg);
      }

      const authorRole: MessageAuthorRole = activeRole === 'advisor' ? 'advisor' : 'owner';

      try {
        await sendMessage(
          { context: stableContext, refCollection: refCollection ?? null, text },
          { scopeUid: effectiveScopeUid, authorUid: currentUser.uid, authorRole },
        );
        setError(null);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'useMessages.send: send failed.';
        setError(message);
        throw err instanceof Error ? err : new Error(message);
      }

      // §3.3 own-message-unread compensation: getThreadUnreadCount/getGlobalUnreadCount
      // cannot filter out `authorUid` server-side (Firestore second-inequality limit),
      // so the sender's own just-sent message would otherwise show as unread. Advance
      // both cursors to now AFTER the send has already succeeded. A cursor-advance
      // failure must NOT turn a successful send into an error — log and continue.
      const readerUid = currentUser.uid;
      try {
        await markThreadRead(stableContext, effectiveScopeUid, readerUid);
        await markGlobalRead(effectiveScopeUid, readerUid);
      } catch (err) {
        logger.warn(
          'useMessages.send: post-send cursor advance failed (send itself succeeded)',
          err instanceof Error ? err : new Error(String(err)),
        );
      }
    },
    [currentUser, activeRole, effectiveScopeUid, stableContext, refCollection],
  );

  const markRead = useCallback((): void => {
    if (!currentUser) {
      return;
    }
    markThreadRead(stableContext, effectiveScopeUid, currentUser.uid).catch((err) => {
      logger.warn(
        'useMessages.markRead failed',
        err instanceof Error ? err : new Error(String(err)),
      );
    });
  }, [currentUser, effectiveScopeUid, stableContext]);

  return { messages, loading, error, send, markRead };
}
